import { useAppDispatch, useAppSelector } from "../../../redux/hooks/hooks";
import {
  documentToDownload,
  setDocumentToDownload,
} from "../../../redux/feature/document/download-document-slice";
import { useEffect, useState } from "react";
import {
  TreeViewCheckChangeEvent,
  TreeViewExpandChangeEvent,
} from "@progress/kendo-react-treeview";
import { navigateBack } from "../../../redux/feature/general/navigation-slice";
import { DOCUMENT_PROPERTIES } from "../../../utils/constants/navigation";
import { formatMessage } from "../../../utils/helpers/LanguageHelper";
import { DocumentRoot } from "../../../api/models/document-root";
import { DocumentPartGroupFetcher } from "../../../utils/DocumentPartGroupFetcher";
import { downloadDocumentCopyAsync } from "../../../redux/feature/document/document-slice";
import { store } from "../../../redux/store/store";
import { DownloadDocument } from "../../../api/models/download-document";
import { useHandleLoaderForDownloadCopy } from "./useHandleLoaderForDownloadCopy";

function mapDoc(
  { documentPart, hasReadPermissions }: DocumentRoot,
  shouldBeChecked: boolean
): DownloadDocument {
  return {
    documentId: documentPart.id,
    items: documentPart.subparts?.map((el) => mapDoc(el as DocumentRoot, shouldBeChecked)) || [],
    expanded: false,
    disabled: !hasReadPermissions,
    checked: shouldBeChecked,
    text: documentPart.name,
    hasChildren: documentPart.hasChildren,
  };
}

export function useDownloadCopyPage() {
  useHandleLoaderForDownloadCopy();
  const dispatch = useAppDispatch();
  const rootDocument = useAppSelector(documentToDownload);
  const [treeItems, setTreeItems] = useState<DownloadDocument[]>([]);
  const [lastSelectedIndex, setLastSelectedIndex] = useState<string>("");

  useEffect(() => {
    getDocumentParts(rootDocument.key, true).then((el) => {
      const allItem = {
        documentId: "ALL",
        text: formatMessage("cof.base.selectall"),
        checked: true,
        disabled: false,
        expanded: true,
        hasChildren: true,
        items: el,
      };

      setTreeItems([allItem]);
    });

    return () => {
      store.dispatch(setDocumentToDownload(null));
    };
  }, [rootDocument.key]);

  async function getDocumentParts(documentId: string, shouldBeChecked: boolean) {
    const groupFetcher = new DocumentPartGroupFetcher(documentId);
    const dataPart = await groupFetcher.callChildrenInGroup();

    return dataPart.map((dp) => mapDoc(dp, shouldBeChecked));
  }

  function checkAllParents(itemHierarchicalIndex: string) {
    const hierarchy = itemHierarchicalIndex.split("_").map(Number);
    let currentLevel = treeItems;

    for (let position of hierarchy) {
      const currentEl = currentLevel[position];
      currentLevel = currentEl.items;
      if (!currentEl || currentEl.documentId == "ALL") {
        continue;
      }

      currentEl.checked = true;
    }
  }

  function checkUncheckAllChild(items: DownloadDocument[], check: boolean) {
    items.forEach((item) => {
      if (item.disabled) return;
      item.checked = check;
      if (item.items?.length) checkUncheckAllChild(item.items, check);
    });
  }

  function verifyAllChecked(items: DownloadDocument[]) {
    for (let child of items) {
      if (!child.checked) return false;
      if (!child.items) continue;

      const isChildChecked = verifyAllChecked(child.items);
      if (!isChildChecked) return false;
    }

    return true;
  }

  function getChild(el: DownloadDocument, itemHierarchicalIndex: string) {
    const hierarchy = itemHierarchicalIndex.split("_").map(Number);
    const index = hierarchy.shift();

    const currentEl = el.items[index];

    const newItemHierarchicalIndex = hierarchy.join("_");
    if (!newItemHierarchicalIndex) return currentEl;

    return getChild(currentEl, newItemHierarchicalIndex);
  }

  function handleSelectionWithShift(_: DownloadDocument, itemHierarchicalIndex: string) {
    const lastSelectedHierarchy = lastSelectedIndex.split("_").map(Number);
    const currentSelectedHierarchy = itemHierarchicalIndex.split("_").map(Number);
    const lastSelectedPosition= lastSelectedHierarchy.pop();
    const currentSelectedPosition = currentSelectedHierarchy.pop();

    const isSameHierarchy =
      `${lastSelectedHierarchy.join("_")}` === `${currentSelectedHierarchy.join("_")}` &&
      lastSelectedHierarchy.length == currentSelectedHierarchy.length;

    if (!isSameHierarchy) {
      // CONTROLLARE SE APPARTENGONO ALLA STESSA GERARCHIA => ES: 0_0_0 con 0_1_0 ERRORE

      setLastSelectedIndex("");
      return;
    }

    const [first, last] = [lastSelectedPosition, currentSelectedPosition].sort((a, b) => a - b);

    let commonParent =
      lastSelectedHierarchy.length == 1
        ? treeItems[lastSelectedHierarchy[0]]
        : getChild(treeItems[0], lastSelectedHierarchy.join("_").replace("0_", ""));

    for (let i = first; i <= last; i++) {
      toggleElement(commonParent.items[i], `${lastSelectedHierarchy.join("_")}_${i}`, true);
    }
  }

  const onToggle = ({
    item,
    itemHierarchicalIndex,
    nativeEvent,
  }: TreeViewCheckChangeEvent & { item: DownloadDocument }): void | null => {
    const shouldCheck = !item.checked;

    toggleElement(item, itemHierarchicalIndex, shouldCheck);

    if (nativeEvent.shiftKey && shouldCheck && lastSelectedIndex) {
      handleSelectionWithShift(item, itemHierarchicalIndex);
      treeItems[0].checked = verifyAllChecked(treeItems[0].items);
      return;
    }

    if (!nativeEvent.shiftKey) setLastSelectedIndex(shouldCheck ? itemHierarchicalIndex : "");

    treeItems[0].checked = verifyAllChecked(treeItems[0].items);
  };

  function toggleElement(
    item: DownloadDocument,
    itemHierarchicalIndex: string,
    shouldCheck: boolean
  ) {
    item.checked = shouldCheck;
    if (item.items?.length) checkUncheckAllChild(item.items, shouldCheck);
    if (shouldCheck && itemHierarchicalIndex.includes("_")) checkAllParents(itemHierarchicalIndex);
  }

  async function onExpandChange({ item, itemHierarchicalIndex }: TreeViewExpandChangeEvent) {
    // "Select All" cannot be collapsed
    if (itemHierarchicalIndex === "0") {
      return;
    }

    const hierarchyWithoutAll = itemHierarchicalIndex.replace("0_", "");

    const el = getChild(treeItems[0], hierarchyWithoutAll);
    el.expanded = !el.expanded;

    if (!el.expanded || el.items.length) return;

    el.items = await getDocumentParts(item.documentId, item.checked);
    const newList = JSON.parse(JSON.stringify(treeItems)) as DownloadDocument[];
    console.log({ newList });
    setTreeItems(newList);
  }

  async function download() {
    const payload: DownloadDocument = {
      documentId: rootDocument.key,
      disabled: false,
      checked: true,
      expanded: true,
      items: treeItems[0].items,
      text: rootDocument.text,
      hasChildren: true,
    };

    await downloadDocumentCopyAsync(payload);
  }

  function goBack() {
    dispatch(navigateBack(DOCUMENT_PROPERTIES));
  }

  return {
    rootDocument,
    treeItems,
    onToggle,
    download,
    goBack,
    onExpandChange,
  };
}
